import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import {ChevronLeft, ChevronRight} from '../../../components/Icons';
import {useRemScale} from 'lib/remScale';

import { TYPE, FONTS } from 'style/typography';

const HIGHLIGHT_BORDER_COLOR = 'rgba(197,232,54,0.30)';
const ACCENT_COLOR = '#C5E836';

// Frosted-glass backdrop, web only (matches the top-left Unit detail box).
const WEB_GLASS_STYLE =
  Platform.OS === 'web'
    ? {backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)'}
    : null;

/**
 * A self-contained control palette rendered as a single glass box that slides
 * off the right edge when collapsed, leaving a chevron handle pinned to the
 * edge to reopen it. Open/collapse state and the `openProgress` animation are
 * owned by the caller (see useAutoCollapsePanel); this component only draws the
 * box, its header, and the handle around whatever children it is given.
 */
const CollapsiblePalette = ({
  isOpen,
  openProgress,
  onCollapse,
  onExpand,
  title,
  bottom,
  rightOffset,
  topLimit,
  children,
}) => {
  const {rem} = useRemScale();
  const {height: windowHeight} = useWindowDimensions();
  const [boxWidth, setBoxWidth] = useState(0);
  const styles = createStyles(rem);

  // Web: on short windows (small screens, browser zoom) the box stops short of the top HUD (`topLimit`, its bottom
  // edge) and its buttons scroll inside instead of running under the HUD. Native keeps its layout.
  const maxBoxHeight =
    Platform.OS === 'web' && typeof topLimit === 'number' && topLimit > 0
      ? Math.max(rem(120), windowHeight - (bottom || 0) - topLimit - rem(8))
      : undefined;

  // Slide the box off the right edge (plus a little margin) when collapsed.
  const hiddenX = (boxWidth || rem(80)) + rem(28);
  const translateX = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [hiddenX, 0],
  });
  const boxOpacity = openProgress.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0.35, 1],
  });
  const handleOpacity = openProgress.interpolate({
    inputRange: [0, 0.2, 0.55, 1],
    outputRange: [1, 1, 0.08, 0],
  });

  return (
    <>
      <View
        style={[styles.container, {right: rightOffset, bottom}]}
        pointerEvents="box-none">
        <Animated.View
          pointerEvents={isOpen ? 'box-none' : 'none'}
          onLayout={e => {
            const w = e?.nativeEvent?.layout?.width;
            if (w && Math.abs((boxWidth || 0) - w) > 1) {
              setBoxWidth(w);
            }
          }}
          style={{opacity: boxOpacity, transform: [{translateX}]}}>
          <View style={[styles.box, WEB_GLASS_STYLE, maxBoxHeight ? {maxHeight: maxBoxHeight} : null]}>
            {/* Title bar — tap (or the chevron) to collapse the whole box. */}
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={onCollapse}
              style={styles.header}>
              <Text style={styles.headerLabel} numberOfLines={1}>
                {title}
              </Text>
              <ChevronRight size={rem(16)} color={ACCENT_COLOR} strokeWidth={2.6} />
            </TouchableOpacity>

            <View style={styles.divider} />

            {maxBoxHeight ? (
              <ScrollView
                style={styles.bodyScroll}
                contentContainerStyle={styles.body}
                showsVerticalScrollIndicator={false}>
                {children}
              </ScrollView>
            ) : (
              <View style={styles.body}>{children}</View>
            )}
          </View>
        </Animated.View>
      </View>

      {/* Collapsed handle — pinned to the right edge, reopens the box. */}
      <Animated.View
        pointerEvents={isOpen ? 'none' : 'auto'}
        style={[
          styles.collapsedHandle,
          WEB_GLASS_STYLE,
          {bottom, opacity: handleOpacity},
        ]}>
        <TouchableOpacity
          activeOpacity={0.9}
          disabled={isOpen}
          onPress={onExpand}
          testID="controls-expand"
          accessibilityLabel="Show controls"
          style={styles.collapsedTouchable}>
          <ChevronLeft size={rem(20)} color={ACCENT_COLOR} strokeWidth={2.6} />
        </TouchableOpacity>
      </Animated.View>
    </>
  );
};

const createStyles = rem =>
  StyleSheet.create({
    container: {
      position: 'absolute',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
    },
    box: {
      backgroundColor: 'rgba(15,16,20,0.84)',
      borderWidth: 1,
      borderColor: HIGHLIGHT_BORDER_COLOR,
      borderRadius: rem(16),
      paddingHorizontal: rem(8),
      paddingTop: rem(6),
      paddingBottom: rem(8),
      shadowColor: '#000',
      shadowOpacity: 0.4,
      shadowRadius: rem(14),
      shadowOffset: {width: 0, height: rem(8)},
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: rem(4),
      paddingVertical: rem(3),
    },
    headerLabel: {
      color: 'rgba(197,232,54,0.9)',
      fontSize: rem(TYPE.xs),
      fontFamily: FONTS.body,
      fontWeight: '700',
      marginRight: rem(8),
    },
    divider: {
      alignSelf: 'stretch',
      height: 1,
      backgroundColor: 'rgba(197,232,54,0.18)',
      marginTop: rem(4),
      marginBottom: rem(8),
    },
    body: {
      alignItems: 'center',
    },
    bodyScroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    collapsedHandle: {
      position: 'absolute',
      right: 0,
      zIndex: 42,
      width: rem(30),
      height: rem(64),
      backgroundColor: 'rgba(15,16,20,0.90)',
      borderWidth: 1,
      borderRightWidth: 0,
      borderColor: HIGHLIGHT_BORDER_COLOR,
      borderTopLeftRadius: rem(14),
      borderBottomLeftRadius: rem(14),
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOpacity: 0.35,
      shadowRadius: rem(10),
      shadowOffset: {width: 0, height: rem(6)},
    },
    collapsedTouchable: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

CollapsiblePalette.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  openProgress: PropTypes.object.isRequired,
  onCollapse: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  title: PropTypes.string,
  bottom: PropTypes.number,
  rightOffset: PropTypes.number,
  topLimit: PropTypes.number,
  children: PropTypes.node,
};

CollapsiblePalette.defaultProps = {
  title: 'Controls',
  bottom: 0,
  rightOffset: 0,
  children: null,
};

export default CollapsiblePalette;
